import { Routes } from '@angular/router';

import { NgxPermissionsGuard } from 'ngx-permissions';
import { PageName } from '~console/app/~shared/models/shared.models';
import { Permission } from '~core/models/roles.models';
import { AuthLayoutComponent } from './auth/layouts/auth-layout/auth-layout.component';
import { OnboardingLayoutComponent } from './auth/layouts/onboarding-layout/onboarding-layout.component';
import { ForgotPasswordComponent } from './auth/pages/forgot-password/forgot-password.component';
import { LoginComponent } from './auth/pages/login/login.component';
import { NtdComponent } from './auth/pages/ntd/ntd.component';
import { ResetPasswordComponent } from './auth/pages/reset-password/reset-password.component';
import { SignupPageComponent } from './auth/pages/signup/signup-page.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { AUTHENTICATED_GUARD } from './~shared/guards/authenticated.guard';
import { DASHBOARD_GUARD } from './~shared/guards/dashboard.guard';
import { NON_ADMIN_GUARD } from './~shared/guards/non-admin.guard';
import { NON_AUTHENTICATED_GUARD } from './~shared/guards/non-authenticated.guard';
import { ONBOARDING_COMPLETED_GUARD } from './~shared/guards/onboarding.guard';
import { unsavedChangesGuard } from './~shared/guards/unsaved-changes.guard';
import { ContentWrapperComponent } from './~shared/layouts/content-wrapper/content-wrapper.component';
import { MigrationLayoutComponent } from './auth/layouts/migration-layout/migration-layout.component';

export const routes: Routes = [
  {
    path: 'auth',
    component: AuthLayoutComponent,
    children: [
      {
        path: 'signin',
        children: [
          {
            path: '',
            component: LoginComponent,
            data: {
              pageName: PageName.login
            }
          },
          {
            path: 'otp',
            canActivate: [NON_AUTHENTICATED_GUARD],
            loadComponent: () =>
              import('./auth/pages/login-otp/login-otp.component').then(
                m => m.LoginOtpComponent
              ),
            data: {
              pageName: PageName.signInOTP
            }
          }
        ]
      },
      {
        path: 'ntd-verification',
        component: NtdComponent,
        data: {
          pageName: PageName.ntdVerification
        }
      },
      {
        path: 'forgot-password',
        canActivate: [NON_AUTHENTICATED_GUARD],
        component: ForgotPasswordComponent,
        data: {
          pageName: PageName.forgotPassword
        }
      },
      {
        path: 'reset-password',
        canActivate: [NON_AUTHENTICATED_GUARD],
        component: ResetPasswordComponent,
        data: {
          pageName: PageName.resetPassword
        }
      },
      {
        path: 'signup',
        canActivate: [NON_AUTHENTICATED_GUARD],
        component: SignupPageComponent,
        data: {
          pageName: PageName.signup
        }
      },
      {
        path: 'password/change',
        loadComponent: () =>
          import('./auth/pages/password-init/password-init.component').then(
            m => m.PasswordInitComponent
          ),
        data: {
          pageName: PageName.passwordInit
        }
      }
    ]
  },
  {
    path: 'verification',
    loadComponent: () =>
      import('./public-verification/layouts/public-verification-layout/public-verification-layout.component').then(
        m => m.PublicVerificationLayoutComponent
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'invoice'
      },
      {
        path: 'invoice',
        loadComponent: () =>
          import('./public-verification/pages/invoice-verification/invoice-verification.component').then(
            m => m.InvoiceVerificationComponent
          ),
        data: {
          pageName: PageName.publicInvoiceVerification
        }
      },
      {
        path: 'ncc',
        loadComponent: () =>
          import('./public-verification/pages/company-verification/company-verification.component').then(
            m => m.CompanyVerificationComponent
          ),
        data: {
          pageName: PageName.publicCompanyVerification
        }
      },
      {
        path: 'receipt',
        loadComponent: () =>
          import('./public-verification/pages/receipt-verification/receipt-verification-page.component').then(
            m => m.ReceiptVerificationPageComponent
          ),
        data: {
          pageName: PageName.publicReceiptVerification
        }
      }
    ]
  },
  {
    path: '',
    component: ContentWrapperComponent,
    canActivate: [
      AUTHENTICATED_GUARD,
      ONBOARDING_COMPLETED_GUARD,
      DASHBOARD_GUARD
    ],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./dashboard/pages/dashboard/dashboard.component').then(
            m => m.DashboardComponent
          ),
        data: {
          pageName: PageName.dashboard
        }
      },
      {
        path: 'audit-logs',
        canActivate: [NgxPermissionsGuard],
        loadComponent: () =>
          import('./audit-logs/pages/audit-logs/audit-logs.component').then(
            m => m.AuditLogsComponent
          ),
        data: {
          pageName: PageName.auditLogs,
          breadcrumbs: 'Audit logs',
          permissions: {
            only: [Permission.READ_AUDIT_LOG],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'api-integration',
        canActivate: [NgxPermissionsGuard],
        loadComponent: () =>
          import('./api-integration/pages/api-integration/api-integration.component').then(
            m => m.ApiIntegrationComponent
          ),
        data: {
          pageName: PageName.apiIntegration,
          breadcrumbs: 'Intégration API',
          permissions: {
            only: [Permission.READ_API_KEY],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'invoices',
        canActivate: [NgxPermissionsGuard],
        data: {
          pageName: PageName.invoices,
          breadcrumbs: 'Factures',
          permissions: {
            only: [Permission.READ_INVOICE],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./invoices/pages/invoice/invoice.component').then(
                m => m.InvoiceComponent
              ),
            data: {
              pageName: PageName.invoices,
              breadcrumbs: 'Factures'
            }
          },
          {
            path: 'generate/:invoiceId',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./invoices/pages/invoices-generation/invoices-generation.component').then(
                m => m.InvoicesGenerationComponent
              ),
            data: {
              pageName: PageName.invoicesGeneration,
              breadcrumbs: 'Génération de la facture'
            }
          },
          {
            path: 'generate-credit-note/:invoiceId',
            loadComponent: () =>
              import('./invoices/pages/generation-credit-note/generation-credit-note.component').then(
                m => m.GenerationCreditNoteComponent
              ),
            data: {
              pageName: PageName.invoicesGenerationCreditNote,
              breadcrumbs: 'Génération de la facture'
            }
          },
          {
            path: 'issuing',
            loadComponent: () =>
              import('./invoices/pages/invoices-issuing/invoices-issuing.component').then(
                m => m.InvoicesIssuingComponent
              ),
            data: {
              pageName: PageName.invoicesIssuing,
              breadcrumbs: 'Emission de la facture'
            }
          }
        ]
      },
      {
        path: 'receipts',
        canActivate: [NgxPermissionsGuard],
        loadComponent: () =>
          import('./receipts/pages/receipts/receipts.component').then(
            m => m.ReceiptsComponent
          ),
        data: {
          pageName: PageName.receipts,
          breadcrumbs: 'Reçus',
          permissions: {
            only: [Permission.READ_RECEIPT],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'customer-supplier',
        canActivate: [NgxPermissionsGuard],
        data: {
          breadcrumbs: 'Partenaires',
          permissions: {
            only: [Permission.READ_CUSTOMER_SUPPLIER],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./customer-supplier/pages/customer-supplier/customer-supplier.component').then(
                m => m.CustomerSupplierComponent
              ),
            data: {
              pageName: PageName.customerSupplier
            }
          },
          {
            path: ':customerSupplierId/billing-address',
            loadComponent: () =>
              import('./customer-supplier/pages/partner-billing-address/partner-billing-address.component').then(
                m => m.PartnerBillingAddressComponent
              ),
            data: {
              pageName: PageName.partnerBillingAddress,
              breadcrumbs: 'Adresse de facturation'
            }
          }
        ]
      },
      {
        path: 'users',
        canActivate: [NgxPermissionsGuard],
        data: {
          breadcrumbs: 'Gestion des utilisateurs',
          permissions: {
            only: [Permission.READ_USER],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./users/pages/users/users.component').then(
                m => m.UsersComponent
              ),
            data: {
              pageName: PageName.users,
              breadcrumbs: 'Gestion des utilisateurs'
            }
          },
          {
            path: 'roles-and-permissions',
            loadComponent: () =>
              import('./users/pages/user-habilitations/user-habilitations.component').then(
                m => m.UserHabilitationsComponent
              ),
            data: {
              pageName: PageName.ability,
              breadcrumbs: 'Roles et permissions',
              permissions: {
                only: [Permission.READ_ROLE],
                redirectTo: '/'
              }
            }
          },
          {
            path: ':userId/profils',
            loadComponent: () =>
              import('./users/pages/users-profile/users-profile.component').then(
                m => m.UsersProfileComponent
              ),
            data: {
              pageName: PageName.userProfil,
              breadcrumbs: 'Profil Utilisateur',
              permissions: {
                only: [Permission.READ_ORGANIZATION_USER],
                redirectTo: '/'
              }
            }
          }
        ]
      },
      {
        path: 'articles',
        canActivate: [NgxPermissionsGuard],
        data: {
          breadcrumbs: 'Gestion des articles',
          permissions: {
            only: [Permission.READ_PRODUCT],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./product/pages/product/product.component').then(
                m => m.ProductComponent
              ),
            data: {
              pageName: PageName.product
            }
          },
          {
            path: ':productId/sales-formats',
            canActivate: [NgxPermissionsGuard],
            loadComponent: () =>
              import('./sale-format/pages/sale-format/sale-format.component').then(
                m => m.SaleFormatComponent
              ),
            data: {
              pageName: PageName.saleFormat,
              breadcrumbs: 'Formats de vente',
              permissions: {
                only: [Permission.READ_SALE_FORMAT],
                redirectTo: '/'
              }
            }
          }
        ]
      },

      {
        path: 'unite-mesures',
        canActivate: [NgxPermissionsGuard],
        loadComponent: () =>
          import('./unit-measure/pages/unit-measure/unit-measure.component').then(
            m => m.UnitMeasureComponent
          ),
        data: {
          pageName: PageName.unitMeasure,
          breadcrumbs: 'Unités de mesure',
          permissions: {
            only: [Permission.READ_UNIT_MEASURE],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'sales-point',
        canActivate: [NgxPermissionsGuard],
        loadComponent: () =>
          import('./sales-point/pages/sales-point/sales-point.component').then(),
        data: {
          pageName: PageName.salesPoint,
          breadcrumbs: 'Points de vente',
          permissions: {
            only: [Permission.READ_SALE_POINT],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'stickers',
        canActivate: [NgxPermissionsGuard],
        data: {
          breadcrumbs: 'Vue d’ensemble du compte sticker',
          pageName: PageName.stickers,
          permissions: {
            only: [Permission.READ_STICKER],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./stickers/page/stickers/stickers.component').then(
                m => m.StickersComponent
              )
          },
          {
            path: 'checkout/succeed',
            loadComponent: () =>
              import('./stickers/page/stickers-payment-succeed/stickers-payment-succeed.component').then(
                m => m.StickersPaymentSucceedComponent
              )
          }
        ]
      },
      {
        path: 'ternes',
        canActivate: [NgxPermissionsGuard],
        data: {
          breadcrumbs: 'Gestion des TERNES',
          pageName: PageName.ternes,
          permissions: {
            only: [Permission.READ_TERNES],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./tern-management/page/tern-management/tern-management.component').then(
                m => m.TernManagementComponent
              )
          }
        ]
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./settings/layouts/settings-layout.component').then(
            m => m.SettingsLayoutComponent
          ),
        data: {
          breadcrumbs: 'Gestion du compte'
        },
        children: [
          {
            path: 'account',
            loadComponent: () =>
              import('./settings/pages/settings-account/settings-account.component').then(
                m => m.SettingsAccountComponent
              ),
            data: {
              pageName: PageName.accountProfile,
              breadcrumbs: 'Informations du compte'
            }
          },
          {
            path: 'security',
            loadComponent: () =>
              import('./settings/pages/settings-security/settings-security.component').then(
                m => m.SettingsSecurityComponent
              ),
            data: {
              pageName: PageName.accountSecurity,
              breadcrumbs: 'Sécurité du compte'
            }
          },
          {
            path: 'enterprise-infos',
            loadComponent: () =>
              import('./settings/pages/settings-enterprise-infos/settings-enterprise-infos.component').then(
                m => m.SettingsEnterpriseInfosComponent
              ),
            data: {
              pageName: PageName.accountEnterpriseInfos,
              breadcrumbs: "Informations de l'entreprise"
            }
          }
        ]
      },
      {
        path: 'organizations',
        canActivate: [NgxPermissionsGuard],
        loadComponent: () =>
          import('./organizations/layouts/organizations-layout.component').then(
            m => m.OrganizationsLayoutComponent
          ),

        data: {
          headerTitle: "Gestion d'organisation",
          breadcrumbs: 'Mon organisation',
          permissions: {
            only: [Permission.READ_ORGANIZATION],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            redirectTo: 'infos',
            pathMatch: 'prefix'
          },
          {
            path: 'infos',
            loadComponent: () =>
              import('./organizations/pages/organization-infos/organizations-infos.component').then(
                m => m.OrganizationsInfosComponent
              ),
            data: {
              pageName: PageName.organizationInfos
            }
          },
          {
            path: 'currencies',
            loadComponent: () =>
              import('./organizations/pages/organizations-currencies/organizations-currencies.component').then(
                m => m.OrganizationsCurrenciesComponent
              ),
            data: {
              pageName: PageName.currencies
            }
          },
          {
            path: 'invoices',
            loadComponent: () =>
              import('./organizations/pages/organization-invoice/organization-invoice.component').then(
                m => m.OrganizationInvoiceComponent
              ),
            data: {
              pageName: PageName.organizationInvoice
            }
          },
          {
            path: 'security',
            loadComponent: () =>
              import('./organizations/pages/settings-security/organizations-settings-security.component').then(
                m => m.OrganizationsSettingsSecurityComponent
              ),
            data: {
              pageName: PageName.organizationSecurity
            }
          },
          {
            path: 'tax-exemptions',
            loadComponent: () =>
              import('./organizations/pages/organizations-tax-exemptions/organizations-tax-exemptions.component').then(
                m => m.OrganizationsTaxExemptionsComponent
              ),
            data: {
              pageName: PageName.taxExemptions
            }
          }
        ]
      },
      {
        path: 'import-task',
        loadComponent: () =>
          import('./import-task/pages/import-task/import-task.component').then(
            m => m.ImportTaskComponent
          ),
        data: {
          pageName: PageName.importTask,
          breadcrumbs: 'Tâche en cours',
          permissions: {
            only: [Permission.READ_IMPORT_TASK],
            redirectTo: '/'
          }
        }
      },
      {
        path: 'establishments',
        canActivate: [NgxPermissionsGuard],
        data: {
          breadcrumbs: 'Gestions des établissements',
          permissions: {
            only: [Permission.READ_ESTABLISHMENT],
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./establishment/pages/establishment/establishment.component').then(
                m => m.EstablishmentComponent
              ),
            data: {
              pageName: PageName.establishment,
              breadcrumbs: 'Gestions des établissements'
            }
          },
          {
            path: 'creation',
            loadComponent: () =>
              import('./establishment/pages/establishment-creation/establishment-creation.component').then(
                m => m.EstablishmentCreationComponent
              ),
            data: {
              pageName: PageName.establishment,
              breadcrumbs: 'Gestions des établissements'
            }
          },
          {
            path: ':establishmentId/modification',
            loadComponent: () =>
              import('./establishment/pages/establishment-creation/establishment-creation.component').then(
                m => m.EstablishmentCreationComponent
              ),
            data: {
              pageName: PageName.establishment,
              breadcrumbs: 'Gestions des établissements'
            }
          },
          {
            path: ':establishmentId/sales-point',
            canActivate: [NgxPermissionsGuard],
            loadComponent: () =>
              import('./sales-point/pages/sales-point/sales-point.component').then(
                m => m.SalesPointComponent
              ),
            data: {
              pageName: PageName.salesPoint,
              breadcrumbs: 'Points de vente',
              permissions: {
                only: [Permission.READ_SALE_POINT],
                redirectTo: '/'
              }
            }
          }
        ]
      }
    ]
  },

  {
    path: '',
    component: OnboardingLayoutComponent,
    canActivate: [AUTHENTICATED_GUARD, NON_ADMIN_GUARD],
    children: [
      {
        path: 'onboarding',
        loadComponent: () =>
          import('./auth/pages/onboarding/onboarding.component').then(
            m => m.OnboardingComponent
          ),
        pathMatch: 'full',
        data: {
          pageName: PageName.onboarding
        }
      }
    ]
  },
  {
    path: '',
    component: MigrationLayoutComponent,
    canActivate: [AUTHENTICATED_GUARD, NON_ADMIN_GUARD],
    children: [
      {
        path: 'onboarding/migration',
        loadComponent: () =>
          import('./auth/pages/migration-onboarding/migration-onboarding.component').then(
            m => m.MigrationOnboardingComponent
          ),
        data: {
          pageName: PageName.onboarding
        }
      }
    ]
  },
  {
    path: 'cgu',
    loadComponent: () =>
      import('./auth/pages/cgu-fne/cgu-fne.component').then(
        m => m.CguFneComponent
      ),
    data: {
      pageName: PageName.cgu
    }
  },
  {
    path: '**',
    component: ContentWrapperComponent,
    canActivate: [AUTHENTICATED_GUARD],
    children: [
      {
        path: '',
        component: NotFoundComponent
      }
    ]
  }
];
